import { useState, useEffect, useMemo } from "react";
import { useTran, useFormDirty } from "@/hooks";
import AppDialog from "@/components/common/AppDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EntryRowTable from "../entries/EntryRowTable";
import {
  useDisclosureStore,
  useEntryUIStore,
  useValidationStore,
} from "../../hooks/useDataEntryStore";
import { updateDataEntry } from "../../operations/entry.ops";
import { observer } from "mobx-react-lite";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";

const toEditable = (entry) => ({
  id: entry._id,
  label: entry.label || "",
  reportingMonth: entry.reportingDate || "",
  entryDate: entry.entryDate || "",
  value: entry.value ?? "",
  unit: entry.unit || "",
  year: entry.year ? String(entry.year) : "",
  location: entry.location || "",
  category: entry.category || "",
  denominator: entry.denominator || "",
  remark: entry.remark || "",
});

const EditEntryDialog = observer(() => {
  const { t } = useTran();
  const { activeDisclosure } = useDisclosureStore();
  const { editingEntry, updatingEntry, setEditingEntry } = useEntryUIStore();
  const { errors, validateEntries, clearErrors, clearEntryFieldError } =
    useValidationStore();

  const { code, title, inputType } = activeDisclosure || {};
  const [entries, setEntries] = useState([]);
  const [selectedReportingYear, setSelectedReportingYear] = useState(null);

  const { reportingYears } = reportingYearStore;

  useEffect(() => {
    if (editingEntry) {
      setEntries([toEditable(editingEntry)]);
    }
  }, [editingEntry]);

  const getYearsInReportingYear = (reportingYear = {}) => {
    const startYear = Number(reportingYear.startYear);
    const endYear = Number(reportingYear.endYear ?? reportingYear.startYear);
    if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) return [];

    const years = [];
    for (let year = startYear; year <= endYear; year += 1) {
      years.push(year);
    }
    return years;
  };

  useEffect(() => {
    if (!editingEntry?.reportingDate || !reportingYears?.length) {
      return;
    }

    const reportingDateYear = Number(
      String(editingEntry.reportingDate).slice(0, 4),
    );

    const selectedReporting = reportingYears.find((reportingYear) =>
      getYearsInReportingYear(reportingYear).includes(
        Number(reportingDateYear),
      ),
    );

    setSelectedReportingYear(selectedReporting || null);
  }, [editingEntry, reportingYears]);

  const open = !!editingEntry;
  const formValues = useMemo(() => ({ entries }), [entries]);
  const isDirty = useFormDirty(formValues, open);

  const updateEntry = (id, field, value) => {
    clearEntryFieldError(id, field);
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    );
  };

  const doClose = () => {
    setEditingEntry(null);
    clearErrors();
  };

  const handleClose = () => {
    doClose();
  };

  const handleSave = async () => {
    if (
      !validateEntries(entries, inputType, {
        validations: activeDisclosure?.validations,
      })
    )
      return;

    const { id, reportingMonth, ...rest } = entries[0];
    const hasRemark = !!rest.remark;
    const hasValue = !!rest.value;
    const payload = {
      ...rest,
      ...(reportingMonth ? { reportingDate: reportingMonth } : {}),
    };
    if (hasRemark && !hasValue) {
      payload.unit = null;
    }

    const success = await updateDataEntry(id, payload);
    if (success) doClose();
  };

  return (
    <AppDialog
      open={!!editingEntry}
      onOpenChange={(v) => !v && handleClose()}
      isDirty={isDirty}
      header={
        <div className="flex items-center gap-2">
          {code && <Badge>{code}</Badge>}
          <span className="text-lg font-semibold">{title}</span>
        </div>
      }
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button disabled={updatingEntry} onClick={handleSave}>
            {t("Save Changes")}
          </Button>
        </div>
      }
    >
      <div className="rounded-lg border border-border overflow-hidden">
        <EntryRowTable
          entries={entries}
          inputType={inputType}
          unitOptions={activeDisclosure?.unitOptions || []}
          disclosure={activeDisclosure}
          errors={errors}
          showInlineErrors
          onUpdate={updateEntry}
          onRemove={() => {}}
          masterDataYear={
            editingEntry?.reportingDate
              ? new Date(editingEntry.reportingDate).getFullYear()
              : activeDisclosure?.masterDataYears?.[0]
          }
          selectedReportingYear={selectedReportingYear}
          isEdit={true}
        />
      </div>
    </AppDialog>
  );
});

export default EditEntryDialog;
